<script setup lang="ts">
  import { computed, ref } from 'vue';

  import type { SMSTemplateSchema } from '@stuut/api-sdk/models';
  import { Badge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
  } from '@ui/components/command';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';

  const props = defineProps<{
    templates: SMSTemplateSchema[];
  }>();

  const modelValue = defineModel<string | null>({ default: null });

  const isTemplateSelectOpen = ref(false);

  const selectedTemplateName = computed(() => {
    if (!modelValue.value) return null;

    return props.templates.find((t) => t.id === modelValue.value)?.name;
  });
</script>

<template>
  <Popover v-model:open="isTemplateSelectOpen">
    <PopoverTrigger as-child>
      <Button
        v-bind="$attrs"
        class="justify-between truncate space-x-2.5 font-normal !h-9"
        variant="outline"
        role="combobox"
        size="sm"
        :aria-expanded="isTemplateSelectOpen"
      >
        <span class="text-xs truncate">{{ selectedTemplateName ?? 'Select Template' }}</span>
        <i class="fa far fa-chevron-down text-xs" />
      </Button>
    </PopoverTrigger>

    <PopoverContent class="max-w-[350px] p-0" align="start">
      <Command
        :filter-function="
          (list, term) => {
            term = term.toLowerCase();
            return (list as Record<string, any>[]).filter((i) =>
              i.name?.toLowerCase()?.includes(term),
            );
          }
        "
      >
        <CommandInput placeholder="Search templates..." />
        <CommandEmpty>No templates found.</CommandEmpty>
        <CommandList>
          <CommandGroup>
            <CommandItem
              v-for="template in templates"
              :key="template.id"
              class="flex gap-2 justify-between"
              :value="template"
              @select="
                () => {
                  isTemplateSelectOpen = false;
                  modelValue = template.id;
                }
              "
            >
              <div class="flex gap-2 w-[95%]">
                <span class="text-nowrap truncate">
                  {{ template.name || 'New SMS Template' }}
                </span>
                <Badge v-if="template.isDefault" variant="open" size="xs"> Default </Badge>
              </div>

              <i v-if="template.id === modelValue" class="fa fa-check" />
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
